import { useSession } from "@clerk/clerk-react";
import { useState, useCallback, useEffect } from "react";
import { UniversalPickerModalProps } from "./types";
import { SEARCH_PLACEHOLDERS } from "./constants/pickerConstants";
import { useModalState } from "./hooks/useModalState";
import { usePickerData } from "./hooks/usePickerData";
import { usePickerSearch } from "./hooks/usePickerSearch";
import { PickerModal } from "./components/PickerModal";
import { PickerSearch } from "./components/PickerSearch";
import { PickerContent } from "./components/PickerContent";

export default function UniversalPickerModal({
  isOpen,
  onClose,
  mode,
  title,
  onConfirm,
  config = {},
}: UniversalPickerModalProps) {
  const { session } = useSession();

  // State management
  const { selected, searchTerm, setSearchTerm } = useModalState(
    isOpen,
    config,
    mode,
  );

  // Multi-select state management
  const [multiSelectState, setMultiSelectState] = useState<string[]>([]);

  // Initialize multi-select state when modal opens
  useEffect(() => {
    if (isOpen && config.multiSelect) {
      setMultiSelectState(
        Array.isArray(config.defaultValue) ? [...config.defaultValue] : [],
      );
    }
  }, [isOpen, config.multiSelect]); // Only reset when modal opens, not when defaultValue changes

  // Stap 1 van de onderdeel-picker: het renovatieproject.
  //
  // Zonder project valt er geen onderdeel te kiezen — de lijst hangt eraan vast.
  // Vroeger stond er dan enkel "Selecteer eerst een renovatieproject" en was de
  // knop dus dood: je moest het venster sluiten, eerst de projectkolom invullen
  // en opnieuw beginnen. Nu kiest hetzelfde venster eerst het project en gaat
  // het daarna door naar de onderdelen.
  const [gekozenProject, setGekozenProject] = useState<any>(null);

  useEffect(() => {
    if (!isOpen) setGekozenProject(null);
  }, [isOpen]);

  const projectIdVanTicket = config.currentRenovationProjectId;
  // Kiest deze picker op dit moment een project i.p.v. een onderdeel?
  const kiestProjectEerst =
    mode === "project-part" && !projectIdVanTicket && !gekozenProject?.id;
  const effectiveMode = kiestProjectEerst ? "renovation-project" : mode;
  const effectiveConfig =
    mode === "project-part" && !projectIdVanTicket && gekozenProject?.id
      ? { ...config, currentRenovationProjectId: gekozenProject.id }
      : config;
  const effectiveTitle = kiestProjectEerst
    ? "Selecteer eerst het renovatieproject"
    : mode === "project-part" && gekozenProject?.title
      ? `Selecteer onderdeel in ${gekozenProject.title}`
      : title;

  // State for input modes that need manual confirmation
  const [inputValue, setInputValue] = useState<any>(config.defaultValue);

  // Initialize input value when modal opens
  useEffect(() => {
    if (
      isOpen &&
      (mode === "text-input" ||
        mode === "number-input" ||
        mode === "document-date" ||
        mode === "expiration-date")
    ) {
      setInputValue(config.defaultValue);
    }
  }, [isOpen, mode, config.defaultValue]);

  const handleSelect = useCallback(
    (value: any) => {
      // Stap 1 van de onderdeel-picker: het gekozen project bewaren en in
      // hetzelfde venster doorgaan naar de onderdelen. Bewust niet bevestigen —
      // de gebruiker vroeg om een onderdeel, niet om een project.
      //
      // "Verwijderen" (`value == null`) hoort daar **niet** bij: dat is geen
      // projectkeuze maar de wens om het onderdeel los te maken, en die moet ook
      // in deze stap werken. Anders slikt de picker de klik op en lijkt
      // verwijderen stuk.
      if (kiestProjectEerst && value != null) {
        setGekozenProject(value);
        setSearchTerm("");
        return;
      }

      // Onderdeel gekozen ná stap 1: het project gaat mee terug, zodat de
      // oproeper beide op het ticket kan zetten. Enkel het onderdeel bewaren zou
      // een ticket opleveren met een onderdeel maar zonder project.
      // Bij "verwijderen" (`value == null`) valt er niets te koppelen.
      if (mode === "project-part" && gekozenProject?.id && value != null) {
        onConfirm({ ...value, renovationProjectId: gekozenProject.id });
        return;
      }

      // Input modes: just update state, don't confirm yet
      if (
        mode === "text-input" ||
        mode === "number-input" ||
        mode === "document-date" ||
        mode === "expiration-date"
      ) {
        setInputValue(value);
        return;
      }

      // Multi-select mode: toggle selection
      if (config.multiSelect) {
        // For building/unit modes, extract the ID from the object
        const toggleValue = (mode === "unit" || mode === "building") && value?.id ? value.id : value;
        setMultiSelectState((prev) => {
          const isSelected = prev.includes(toggleValue);
          if (isSelected) {
            return prev.filter((v) => v !== toggleValue);
          } else {
            return [...prev, toggleValue];
          }
        });
      } else {
        // Single select: immediately confirm
        onConfirm(value);
      }
    },
    [
      config.multiSelect,
      mode,
      onConfirm,
      kiestProjectEerst,
      gekozenProject,
      setSearchTerm,
    ],
  );

  // Separate callback for auto-selection that does NOT auto-confirm
  // This prevents the modal from auto-closing when data is loaded
  const handleAutoSelect = useCallback(
    (value: any) => {
      // For auto-selection, we do NOT want to immediately confirm
      // This callback should only be used by usePickerData for scenarios
      // like rental-contract where we want to auto-select but still show the modal
      // For now, we can just ignore auto-selection for supplier mode
      // Only the rental-contract mode should use this
      if (mode === 'rental-contract') {
        onConfirm(value);
      }
      // For other modes like supplier, do nothing - wait for user to click
    },
    [mode, onConfirm],
  );

  // Handle newly created supplier - select it immediately
  const handleSupplierCreated = useCallback(
    (supplier: any) => {
      onConfirm(supplier);
    },
    [onConfirm],
  );

  const handleConfirmMultiSelect = useCallback(() => {
    onConfirm(multiSelectState);
  }, [multiSelectState, onConfirm]);

  // Data fetching - pass handleAutoSelect instead of handleSelect
  const {
    contacts,
    suppliers,
    frequentSuppliers,
    assets,
    apiData,
    epics,
    renovationProjects,
    projectParts,
    resources,
    isLoadingContacts,
    isLoadingSuppliers,
    isLoadingAssets,
    isLoadingApiData,
    isLoadingEpics,
    // De reno-vlaggen zaten er niet bij, waardoor de picker tijdens het laden
    // "geen resultaten" toonde in plaats van een laadindicator.
    isLoadingRenovationProjects,
    isLoadingProjectParts,
    isLoadingResources,
  } = usePickerData(
    isOpen,
    effectiveMode,
    searchTerm,
    effectiveConfig,
    handleAutoSelect,
  );

  // Search and filtering
  const filteredData = usePickerSearch(
    effectiveMode,
    searchTerm,
    effectiveConfig,
    suppliers,
    contacts,
    assets,
    apiData,
    epics,
    renovationProjects,
    projectParts,
    resources,
  );

  // Select all items (for multi-select modes)
  const handleSelectAll = useCallback(() => {
    // Extract IDs for object-based modes (building, unit, supplier, etc.)
    const allIds = filteredData.map((item: any) => item.id || item).filter(Boolean);
    setMultiSelectState(allIds);
  }, [filteredData]);

  // Deselect all items
  const handleDeselectAll = useCallback(() => {
    setMultiSelectState([]);
  }, []);

  // Don't render building/unit picker if session is not available yet
  if ((mode === "building" || mode === "unit") && !session) {
    return (
      <PickerModal isOpen={isOpen} onClose={onClose} title={title as string}>
        <div className="text-neutral-secondary-mid py-4 text-center">
          Laden...
        </div>
      </PickerModal>
    );
  }

  const getSearchPlaceholder = () => {
    // In stap 1 hoort de placeholder bij het project, niet bij het onderdeel.
    if (kiestProjectEerst) return SEARCH_PLACEHOLDERS["renovation-project"];
    return config.searchPlaceholder || SEARCH_PLACEHOLDERS[mode];
  };

  const shouldShowSearch =
    mode !== "document-date" &&
    mode !== "expiration-date" &&
    mode !== "status" &&
    mode !== "text-input" &&
    mode !== "number-input";

  // Modes that require a confirm button (don't auto-confirm on selection)
  const needsConfirmButton =
    config.multiSelect ||
    mode === "text-input" ||
    mode === "number-input" ||
    mode === "document-date" ||
    mode === "expiration-date" ||
    (mode === "custom-tags" && !config.multiSelect);

  // Use multiSelectState for multi-select mode, otherwise use selected
  const effectiveSelected = config.multiSelect ? multiSelectState : selected;

  // Check if confirm button should be disabled (validation failed)
  const isConfirmDisabled = (() => {
    // For input modes, check if inputValue is null/undefined/empty
    if (mode === "text-input") {
      return !inputValue || inputValue.trim().length === 0;
    }
    if (mode === "number-input") {
      return inputValue === null || inputValue === undefined;
    }
    if (mode === "document-date" || mode === "expiration-date") {
      return inputValue === null || inputValue === undefined;
    }
    // For multi-select, disable if nothing selected
    if (config.multiSelect) {
      return multiSelectState.length === 0;
    }
    return false;
  })();

  const handleConfirm = useCallback(() => {
    // Use inputValue for input modes, effectiveSelected for others
    const valueToConfirm =
      mode === "text-input" ||
      mode === "number-input" ||
      mode === "document-date" ||
      mode === "expiration-date"
        ? inputValue
        : effectiveSelected;
    onConfirm(valueToConfirm);
  }, [mode, inputValue, effectiveSelected, onConfirm]);

  return (
    <PickerModal
      isOpen={isOpen}
      onClose={onClose}
      title={effectiveTitle as string}
    >
      {shouldShowSearch && (
        <PickerSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={getSearchPlaceholder()}
        />
      )}

      <PickerContent
        mode={effectiveMode}
        config={effectiveConfig}
        filteredData={filteredData}
        selected={effectiveSelected}
        onSelect={handleSelect}
        isLoading={
          isLoadingContacts ||
          isLoadingSuppliers ||
          isLoadingAssets ||
          isLoadingApiData ||
          isLoadingEpics ||
          isLoadingRenovationProjects ||
          isLoadingProjectParts ||
          isLoadingResources
        }
        searchTerm={searchTerm}
        frequentSuppliers={frequentSuppliers}
        onSupplierCreated={config.allowCreateSupplier ? handleSupplierCreated : undefined}
        onSelectAll={config.multiSelect ? handleSelectAll : undefined}
        onDeselectAll={config.multiSelect ? handleDeselectAll : undefined}
      />

      {/* Confirmation button for modes that need it */}
      {needsConfirmButton && (
        <div className="border-neutral-primary/10 sticky bottom-0 mt-2 border-t bg-white p-3">
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="text-neutral-primary border-neutral-primary/20 hover:bg-neutral-primary/5 rounded-md border px-4 py-2 text-sm transition-colors"
            >
              Annuleren
            </button>
            <button
              onClick={
                config.multiSelect ? handleConfirmMultiSelect : handleConfirm
              }
              disabled={isConfirmDisabled}
              className={`rounded-md px-4 py-2 text-sm transition-colors ${
                isConfirmDisabled
                  ? "bg-neutral-primary/20 text-neutral-primary/40 cursor-not-allowed"
                  : "bg-moss-primary hover:bg-moss-primary/90 text-white"
              }`}
            >
              {config.multiSelect
                ? `Bevestig (${multiSelectState.length} geselecteerd)`
                : "Bevestig"}
            </button>
          </div>
        </div>
      )}
    </PickerModal>
  );
}

// Export types for external use
export type {
  UniversalPickerModalProps,
  UniversalPickerMode,
  PickerConfig,
  DatabaseFileLabel,
} from "./types";
